import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { SecurityTestSuite } from '../utils/SecurityTestSuite';

interface TestResult {
  testName: string;
  passed: boolean;
  error?: string;
  details?: any;
}

interface TestSuiteResults {
  total: number;
  passed: number;
  failed: number;
  successRate: number;
  results: TestResult[];
}

export function SecurityTestRunner() {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<TestSuiteResults | null>(null);
  const [expandedTests, setExpandedTests] = useState<string[]>([]);

  const runTests = async () => {
    setIsRunning(true);
    setResults(null);

    try {
      // Create and run the test suite
      const testSuite = new SecurityTestSuite();
      const testResults = testSuite.runAllTests();
      
      setResults(testResults);
    } catch (error) {
      console.error('Test execution failed:', error);
      setResults({
        total: 0,
        passed: 0,
        failed: 1,
        successRate: 0,
        results: [{
          testName: 'Test Execution',
          passed: false,
          error: `Failed to run tests: ${error}`,
        }]
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleExpandTest = (testName: string) => {
    setExpandedTests(prev => 
      prev.includes(testName) 
        ? prev.filter(name => name !== testName)
        : [...prev, testName]
    );
  };

  const getStatusIcon = (passed: boolean) => {
    return passed ? <CheckIcon color="success" /> : <ErrorIcon color="error" />;
  };

  const getStatusColor = (passed: boolean) => {
    return passed ? 'success' : 'error';
  };

  const getStatusChip = (passed: boolean) => {
    return (
      <Chip
        icon={getStatusIcon(passed)}
        label={passed ? 'Passed' : 'Failed'}
        color={getStatusColor(passed)}
        size="small"
      />
    );
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'error';
  };

  const getSuccessRateIcon = (rate: number) => {
    if (rate >= 90) return <CheckIcon />;
    if (rate >= 70) return <WarningIcon />;
    return <ErrorIcon />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SecurityIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Security Test Suite
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Comprehensive security and validation testing for the compliance platform.
          This test suite validates input sanitization, XSS prevention, rate limiting,
          and other security measures.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={runTests}
            disabled={isRunning}
            size="large"
          >
            {isRunning ? 'Running Tests...' : 'Run Security Tests'}
          </Button>
          
          {results && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={runTests}
              disabled={isRunning}
            >
              Run Again
            </Button>
          )}
        </Box>

        {isRunning && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Running security tests...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {results && (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon color="info" sx={{ mr: 1 }} />
                      <Typography variant="h6">Total Tests</Typography>
                    </Box>
                    <Typography variant="h3" color="primary">
                      {results.total}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">Passed</Typography>
                    </Box>
                    <Typography variant="h3" color="success.main">
                      {results.passed}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">Failed</Typography>
                    </Box>
                    <Typography variant="h3" color="error.main">
                      {results.failed}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {getSuccessRateIcon(results.successRate)}
                      <Typography variant="h6" sx={{ ml: 1 }}>Success Rate</Typography>
                    </Box>
                    <Typography 
                      variant="h3" 
                      color={`${getSuccessRateColor(results.successRate)}.main`}
                    >
                      {results.successRate.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {results && results.failed > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {results.failed} test(s) failed
            </Typography>
            <Typography variant="body2">
              Please review the failed tests below and address any security issues.
            </Typography>
          </Alert>
        )}

        {results && results.failed === 0 && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              All tests passed! 🎉
            </Typography>
            <Typography variant="body2">
              The security implementation is working correctly.
            </Typography>
          </Alert>
        )}
      </Paper>

      {results && (
        <Paper elevation={2}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Test Results ({results.results.length} tests)
            </Typography>
          </Box>
          
          <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
            {results.results.map((result, index) => (
              <Accordion
                key={index}
                expanded={expandedTests.includes(result.testName)}
                onChange={() => handleExpandTest(result.testName)}
                sx={{
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {getStatusIcon(result.passed)}
                    <Typography variant="body1" sx={{ ml: 2, flexGrow: 1 }}>
                      {result.testName}
                    </Typography>
                    {getStatusChip(result.passed)}
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  {result.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Error:</strong> {result.error}
                      </Typography>
                    </Alert>
                  )}
                  
                  {result.details && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Details:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{ 
                          bgcolor: 'grey.100', 
                          p: 1, 
                          borderRadius: 1,
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {JSON.stringify(result.details, null, 2)}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default SecurityTestRunner;

